import React, { memo, useContext, useCallback } from 'react';
import type { NodeProps } from 'reactflow';
import { GraphViewContext } from '../GraphView';
import { BaseNode } from './BaseNode';

interface ActionButtonProps {
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  label: string;
  tooltip: string;
  variant?: 'default' | 'danger';
}
const ActionButton: React.FC<ActionButtonProps> = ({ onClick, label, tooltip, variant = 'default' }) => (
  <button
    onClick={onClick}
    title={tooltip}
    className={`p-2 text-xs rounded transition-colors duration-150
      ${variant === 'danger'
        ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900'
        : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'}`}
  >
    {label}
  </button>
);

export const GroupNode: React.FC<NodeProps> = memo(({ data, selected }) => {
  const handlers = useContext(GraphViewContext);
  if (!handlers) {
    console.error('[GroupNode] Missing GraphViewContext');
    return null;
  }

  const { handleToggleCollapse, collapsedPaths } = handlers;

  const path = data.path ?? [];
  const label = data.label ?? 'Group';
  const lower = label.toLowerCase();
  const isNot = data.isNot ?? lower === 'not';
  const isAllOf = !isNot && lower === 'allof';
  const isAnyOf = !isNot && lower === 'anyof';

  const addCondition = useCallback(() => handlers.handleAddCondition(path), [handlers, path]);
  const addAllOf = useCallback(() => handlers.handleAddAllOfGroup(path), [handlers, path]);
  const addCount = useCallback(() => handlers.handleAddCountCondition(path), [handlers, path]);
  const toggleGroupType = useCallback(() => handlers.handleToggleGroupType(path), [handlers, path]);
  const toggleNot = useCallback(() => handlers.handleToggleNot(path), [handlers, path]);
  const handleEdit = useCallback(
    (e: React.MouseEvent) => { e.stopPropagation(); handlers.onEditNode(path); },
    [handlers, path]
  );
  const handleDelete = useCallback(() => handlers.handleDeleteNode(path), [handlers, path]);

  const bgClasses = isNot
    ? 'from-red-50 to-red-100 dark:from-red-800 dark:to-red-900'
    : isAllOf
    ? 'from-blue-50 to-blue-100 dark:from-blue-800 dark:to-blue-900'
    : isAnyOf
    ? 'from-green-50 to-green-100 dark:from-green-800 dark:to-green-900'
    : 'from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900';
  const ringColor = isNot
    ? 'ring-red-500'
    : isAllOf
    ? 'ring-blue-500'
    : isAnyOf
    ? 'ring-green-500'
    : 'ring-gray-500';

  const serialized = JSON.stringify(path);
  const isCollapsed = collapsedPaths.includes(serialized);
  const toggleCollapse = useCallback(() => handleToggleCollapse(path), [handleToggleCollapse, path]);

  return (
    <BaseNode
      selected={selected}
      bgClasses={bgClasses}
      ringColor={ringColor}
      header={label}
      footer={
        <>
          <ActionButton onClick={toggleCollapse} label={isCollapsed ? '➕' : '➖'} tooltip={isCollapsed ? 'Expand subtree' : 'Collapse subtree'} />
          {!isNot && (
            <>
              <ActionButton onClick={addCondition} label="📝" tooltip="Add Condition" />
              <ActionButton onClick={addAllOf} label="📂" tooltip="Group Condition" />
              <ActionButton onClick={addCount} label="🔢" tooltip="Add Count" />
              {(isAllOf || isAnyOf) && <ActionButton onClick={toggleGroupType} label="🔄" tooltip={`Toggle to ${isAllOf ? 'anyOf' : 'allOf'}`} />}
              <ActionButton onClick={toggleNot} label="🚫" tooltip="Wrap NOT" />
              <ActionButton onClick={handleDelete} label="🗑️" tooltip="Delete" variant="danger" />
            </>
          )}
          {isNot && <ActionButton onClick={toggleNot} label="🗑️" tooltip="Remove NOT" variant="danger" />}
        </>
      }
    >
      {/* No additional content for group nodes */}
      <> </>
    </BaseNode>
  );
});

GroupNode.displayName = 'GroupNode'; 